import { PublicClientApplication } from '@azure/msal-browser';
import axios from 'axios';
import { msalConfig, request } from 'src/mixins/msalHelper';
import { getStore } from 'src/utils/store-bridge';
import validate from 'validate-azure-ad-token';
import {
  getAzureLoginStatus,
  removeAzureToken,
  setAzureToken,
} from '../utils/auth';
import URL from './baseUrl';
import { getAuthConfig, getUploadConfig } from './config';

export function getHostUrl() {
  var hostURL = localStorage.getItem('hostURL');
  if (hostURL == null) {
    localStorage.setItem('hostURL', URL.prodHostUrl);
    hostURL = localStorage.getItem('hostURL');
  }
  return hostURL;
  // return URL.localHostUrl;
  // return URL.testHostUrl;
}

const baseURL = getHostUrl();
const msalInstance = new PublicClientApplication(msalConfig);

async function validateToken(authData) {
  try {
    await validate(authData.accessToken, {
      tenantId: authData.account.idTokenClaims.tid,
      audience: '00000003-0000-0000-c000-000000000000',
      applicationId: authData.account.idTokenClaims.aud,
      scopes: authData.scopes,
    });
    return true;
  } catch (error) {
    return false;
  }
}

export async function logoutAzureUser() {
  getStore()?.dispatch('clearState');
}

export async function RedirectUserPasswordExpired() {
  getStore()?.dispatch('userRedirectPasswordExpired');
}

export const axiosInstance = axios.create({
  baseURL,
});

let tokenPromise = null;

async function refreshToken() {
  if (!tokenPromise) {
    tokenPromise = (async () => {
      const refresh = localStorage.getItem('refresh');
      if (!refresh) {
        return Promise.reject(new Error('No refresh token'));
      }
      try {
        const payload = { refresh };
        const { data } = await axios.post(
          baseURL + 'account/token/refresh/',
          payload,
          { headers: { 'Content-Type': 'application/json' } }
        );
        localStorage.setItem('token', data.access);
        localStorage.setItem('refresh', data.refresh);
        return data.access;
      } catch (error) {
        return Promise.reject(error);
      } finally {
        tokenPromise = null;
      }
    })();
  }

  return tokenPromise;
}

axiosInstance.interceptors.response.use(
  (resp) => resp,
  async (error) => {
    const {
      config,
      response: { status },
    } = error;
    const originalRequest = config;

    if (status === 401) {
      try {
        const token = await refreshToken();
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return axiosInstance(originalRequest);
      } catch (error) {
        return Promise.reject(error);
      }
    } else if (status === 303) {
      RedirectUserPasswordExpired();
    } else if (status === 499) {
      logoutAzureUser(msalInstance);
    } else if (status === 498) {
      let tokenResponse;
      try {
        removeAzureToken();
        tokenResponse = await msalInstance.acquireTokenSilent(request);
        const isTokenValid = await validateToken(tokenResponse);
        setAzureToken();

        if (!isTokenValid) {
          throw new Error('Token Invalid');
        }
      } catch (error) {
        logoutAzureUser(msalInstance);
        return;
      }

      try {
        await axios.post(
          baseURL + 'account/refresh-azure-token/',
          { access_token: tokenResponse.accessToken },
          { headers: { ...getAuthConfig().headers } }
        );
      } catch (e) {
        // ignore and proceed to logout below if needed
      }

      if (getAzureLoginStatus()) {
        return axiosInstance(originalRequest).then(() => {
          location.reload();
        });
      } else {
        logoutAzureUser(msalInstance);
      }
    }

    return Promise.reject(error);
  }
);

async function get(
  url,
  spinner = true,
  queryParams = {},
  config = getAuthConfig()
) {
  if (spinner) getStore()?.dispatch('turnOnSpinner');

  try {
    const { data } = await axiosInstance.get(url, {
      params: { ...queryParams },
      ...config,
    });
    return [null, data];
  } catch (error) {
    return [error];
  } finally {
    if (spinner) getStore()?.dispatch('turnOffSpinner');
  }
}

async function post(url, payload, spinner = true, config = getAuthConfig()) {
  if (spinner) getStore()?.dispatch('turnOnSpinner');

  try {
    const { data } = await axiosInstance.post(url, payload, {
      ...config,
    });
    return [null, data];
  } catch (error) {
    return [error];
  } finally {
    if (spinner) getStore()?.dispatch('turnOffSpinner');
  }
}

async function put(url, payload, spinner = true, config = getAuthConfig()) {
  if (spinner) getStore()?.dispatch('turnOnSpinner');

  try {
    const { data } = await axiosInstance.put(url, payload, {
      ...config,
    });
    return [null, data];
  } catch (error) {
    return [error];
  } finally {
    if (spinner) getStore()?.dispatch('turnOffSpinner');
  }
}

async function patch(
  url,
  payload,
  spinner = true,
  config = getAuthConfig(),
  responseType = 'json'
) {
  if (spinner) getStore()?.dispatch('turnOnSpinner');
  try {
    const { data } = await axiosInstance.patch(url, payload, {
      ...config,
      responseType,
    });
    return [null, data];
  } catch (error) {
    return [error];
  } finally {
    if (spinner) getStore()?.dispatch('turnOffSpinner');
  }
}

async function remove(
  url,
  spinner = true,
  queryParams = {},
  config = getAuthConfig()
) {
  if (spinner) getStore()?.dispatch('turnOnSpinner');

  try {
    const { data } = await axiosInstance.delete(url, {
      params: { ...queryParams },
      ...config,
    });
    return [null, data];
  } catch (error) {
    return [error];
  } finally {
    if (spinner) getStore()?.dispatch('turnOffSpinner');
  }
}

async function upload(
  url,
  payload,
  spinner = true,
  queryParams = {},
  config = getUploadConfig()
) {
  if (spinner) getStore()?.dispatch('turnOnSpinner');

  try {
    const { data } = await axiosInstance.post(url, payload, {
      params: { ...queryParams },
      ...config,
    });
    return [null, data];
  } catch (error) {
    return [error];
  } finally {
    if (spinner) getStore()?.dispatch('turnOffSpinner');
  }
}

export default { get, post, put, patch, remove, upload };
